import Skeleton from '@material-ui/lab/Skeleton';
import { Translation } from '@ps/web-shared-components';
import he from 'he';
import { isUndefined } from 'lodash';
import React from 'react';

import { CMSHTML } from '@swf/cms';
import Link from '@swf/components/Link/Link';
import type { Article, Category } from '@swf/help';
import { SkeletonList } from '@swf/help';

import { getCategoryTitle } from '../../utils';
import Breadcrumb, { BreadcrumbSkeleton } from '../Breadcrumb/Breadcrumb';
import HelpCenterSEO from '../HelpCenterSEO/HelpCenterSEO';
import SideBar, { SideBarSkeleton } from '../SideBar/SideBar';

import {
  TXTWCL_Help_Home,
  TXTWCL_OopsX_Page_not_found,
  TXTWCL_Related_Articles
} from './ArticleDetail.messages.json';
import styles from './ArticleDetail.scss';
import Feedback from './components/Feedback/Feedback';

interface Props {
  categories: Category[];
  articleDetail: Article;
  relatedArticles: Article[];
}

const ArticleDetail: React.FC<Props> = ({ categories, articleDetail, relatedArticles = [] }) => {
  const { title = '', content = '', category = '' } = articleDetail;
  const categoryId = category.substring(category.indexOf('-') + 1);
  const categoryTitle = getCategoryTitle(categories, categoryId);

  const sideNavs = relatedArticles.map(({ id, title, ref }) => ({
    id,
    element: (
      <Link alias="helpCenterArticle" values={{ ref }}>
        {he.decode(title)}
      </Link>
    )
  }));

  return (
    <>
      {title && <HelpCenterSEO title={title} />}
      <section>
        <div className={styles.inner}>
          {category && <Breadcrumb id={categoryId} title={categoryTitle} />}
          <div className={styles.content}>
            <div className={styles.article}>
              <h1 className={styles.title}>{he.decode(title)}</h1>
              {title ? (
                <>
                  <div className={styles.articleBody}>
                    <CMSHTML content={content} />
                  </div>
                  <Feedback article={articleDetail} />
                </>
              ) : (
                !isUndefined(articleDetail) && (
                  <div className={styles.center}>
                    <Translation tagName="h1" {...TXTWCL_OopsX_Page_not_found} />
                    <Link alias={'helpCenter'}>
                      <Translation tagName="h1" {...TXTWCL_Help_Home} />
                    </Link>
                  </div>
                )
              )}
            </div>
            {!!sideNavs.length && (
              <SideBar sideNavs={sideNavs} title={<Translation {...TXTWCL_Related_Articles} />} />
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export const ArticleDetailSkeleton = () => (
  <section>
    <div className={styles.inner}>
      <BreadcrumbSkeleton />
      <div className={styles.content}>
        <div className={styles.article}>
          <Skeleton variant="rect" width={250} height={40} className={styles.title} />
          <div className={styles.articleBody}>
            <SkeletonList
              skeletonSizes={['30%', '35%', '70%', '60%', '25%', '65%', '45%', '85%']}
              height={24}
              className={styles.articleDetailSkeleton}
            />
          </div>
        </div>
        <SideBarSkeleton />
      </div>
    </div>
  </section>
);

export default ArticleDetail;
